/**
 * MedicalHistoryStepCard
 *
 * Dedicated dark card for the "Complete Medical History" plan step.
 * Uses the silk-brown textured design from the Medical History page:
 * progress ring and insight rows.
 */

import React from "react";
import {
  View,
  Text,
  Pressable,
  Image,
  StyleSheet,
  Platform,
} from "react-native";
import Svg, { Circle } from "react-native-svg";
import { LinearGradient } from "expo-linear-gradient";
import {
  Check,
  ArrowRight,
  Sparkles,
  Trophy,
  Pill,
  TestTubes,
  ShieldAlert,
} from "lucide-react-native";
import { cardSurface, FEATURE_CARD_RADIUS, Tag } from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { getMedicalHistoryProgressSummary } from "@src/packages/medical-history/sectionRegistry";
import type { PlanAction } from "../plan.types";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { semanticColors } from "@louise/theme/tokens";
const silkBrown =
  require("@src/apps/moon/assets/images/silk-brown.png") as number;

type Props = {
  stepNumber: number;
  totalSteps: number;
  onAction?: (action: PlanAction) => void;
  onOpenPregnancyTrackingSetup?: () => void;
  showStepPill?: boolean;
  bottomMargin?: number;
};

function useCompletion() {
  const { data: patient, isLoading } = usePatient();
  const summary = getMedicalHistoryProgressSummary(patient);

  if (isLoading || !patient) {
    return {
      isLoading,
      progressPercent: 0,
      completedCategories: 0,
      totalSections: summary.totalSections,
    };
  }

  return {
    isLoading: false,
    progressPercent: summary.progressPercent,
    completedCategories: summary.completedSections,
    totalSections: summary.totalSections,
  };
}

// ─── Progress ring (dark mode) ───

function ProgressRing({
  percent,
  size = 64,
}: {
  percent: number;
  size?: number;
}) {
  const strokeWidth = 6;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - percent / 100);

  return (
    <View style={{ width: size, height: size }}>
      <Svg width={size} height={size}>
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={semanticColors.onDark.fill.subtle}
          strokeWidth={strokeWidth}
          fill="none"
        />
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={
            percent >= 100
              ? semanticColors.status.success
              : semanticColors.onDark.text.secondary
          }
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={`${circumference}`}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </Svg>
      <View
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {percent >= 100 ? (
          <Trophy
            size={20}
            color={semanticColors.status.success}
            fill={semanticColors.status.success}
          />
        ) : (
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 15,
              fontWeight: "700",
              color: semanticColors.onDark.text.primary,
            }}
          >
            {percent}%
          </Text>
        )}
      </View>
    </View>
  );
}

// ─── Main component ───

export default function MedicalHistoryStepCard({
  stepNumber,
  totalSteps,
  onAction,
  showStepPill = true,
  bottomMargin = 16,
}: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("MedicalHistoryStepCard");
  const { isLoading, progressPercent, completedCategories, totalSections } =
    useCompletion();

  const isAllComplete = completedCategories >= totalSections;

  return (
    <View style={{ marginBottom: bottomMargin }}>
      {/* Step number pill */}
      {showStepPill ? (
        <View style={{ marginBottom: 8 }}>
          <Tag
            label={
              isAllComplete
                ? t("homepage.shared.step_n_complete", {
                    params: { stepNumber: String(stepNumber) },
                  })
                : t("homepage.shared.step_n_of_total", {
                    params: {
                      stepNumber: String(stepNumber),
                      totalSteps: String(totalSteps),
                    },
                  })
            }
            icon={isAllComplete ? Check : undefined}
            tone={isAllComplete ? "green" : "neutral"}
            style={{ alignSelf: "flex-start" }}
          />
        </View>
      ) : null}

      {/* Dark card */}
      <Pressable
        onPress={() => {
          capturePress(
            pressLabel(t("homepage.shared.continue_medical_history")),
            "homepage.shared.continue_medical_history",
          );
          onAction?.({
            label: t("homepage.shared.continue_medical_history"),
            variant: "primary",
            screen: "MedicalHistory",
          });
        }}
        style={{
          ...cardSurface({ variant: "feature" }),
          shadowColor: "#000",
          shadowOffset: { width: 0, height: 8 },
          shadowOpacity: 0.12,
          shadowRadius: 24,
          ...(isAllComplete && { opacity: 0.75 }),
        }}
      >
        {/* Silk texture */}
        <Image
          source={silkBrown}
          style={[StyleSheet.absoluteFill, { width: "100%", height: "100%" }]}
          resizeMode="cover"
        />

        {/* Glass overlay */}
        <View
          style={{
            padding: 17,
            backgroundColor: semanticColors.onDark.fill.subtle,
            borderRadius: FEATURE_CARD_RADIUS,
            borderWidth: 1,
            borderColor: semanticColors.onDark.fill.strong,
            ...(Platform.OS === "web"
              ? ({
                  backdropFilter: "blur(40px)",
                  WebkitBackdropFilter: "blur(40px)",
                } as Record<string, string>)
              : {}),
          }}
        >
          {/* Title + ring */}
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 13,
            }}
          >
            <ProgressRing percent={isLoading ? 0 : progressPercent} />
            <View style={{ flex: 1 }}>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 19,
                  lineHeight: 24,
                  fontWeight: "700",
                  color: semanticColors.onDark.text.primary,
                  letterSpacing: -0.3,
                }}
              >
                {t("homepage.medical_history_step.title")}
              </Text>
              {isLoading || isAllComplete ? (
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 12,
                    fontWeight: "500",
                    color: semanticColors.onDark.text.secondary,
                    marginTop: 4,
                    lineHeight: 17,
                  }}
                >
                  {isLoading
                    ? t("homepage.medical_history_step.loading")
                    : t("homepage.medical_history_step.all_sections_complete")}
                </Text>
              ) : (
                <View
                  style={{
                    flexDirection: "row",
                    flexWrap: "wrap",
                    alignItems: "center",
                    columnGap: 5,
                    rowGap: 5,
                    marginTop: 7,
                  }}
                >
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 11.5,
                      fontWeight: "500",
                      color: semanticColors.onDark.text.secondary,
                      lineHeight: 16,
                    }}
                  >
                    {t(
                      "homepage.medical_history_step.complete_history_unlocks",
                    )}
                  </Text>
                  {[
                    {
                      labelKey:
                        "homepage.medical_history_step.unlock_treatment_personalisation",
                      icon: Sparkles,
                    },
                    {
                      labelKey:
                        "homepage.medical_history_step.unlock_medication_safety",
                      icon: Pill,
                    },
                    {
                      labelKey:
                        "homepage.medical_history_step.unlock_risk_screening",
                      icon: ShieldAlert,
                    },
                    {
                      labelKey:
                        "homepage.medical_history_step.unlock_clinic_summaries",
                      icon: TestTubes,
                    },
                  ].map(({ labelKey, icon: Icon }) => (
                    <View
                      key={labelKey}
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 3,
                      }}
                    >
                      <Icon
                        size={11}
                        color={semanticColors.onDark.text.primary}
                        strokeWidth={1.8}
                      />
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 11.5,
                          lineHeight: 16,
                          fontWeight: "700",
                          color: semanticColors.onDark.text.primary,
                        }}
                      >
                        {t(labelKey)}
                      </Text>
                    </View>
                  ))}
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 11.5,
                      fontWeight: "500",
                      color: semanticColors.onDark.text.secondary,
                      lineHeight: 16,
                    }}
                  >
                    {" "}
                    {t("homepage.medical_history_step.and_more")}
                  </Text>
                </View>
              )}
            </View>
          </View>

          {/* CTA button */}
          {!isLoading && !isAllComplete && (
            <View style={{ marginTop: 18 }}>
              <LinearGradient
                colors={[
                  "rgba(255, 255, 255, 0.12)",
                  "rgba(255, 255, 255, 0.06)",
                ]}
                start={{ x: 0, y: 0 }}
                end={{ x: 0, y: 1 }}
                style={{
                  paddingVertical: 12,
                  paddingHorizontal: 20,
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                  borderWidth: 1,
                  borderColor: semanticColors.onDark.fill.subtle,
                  borderRadius: 14,
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 14,
                    fontWeight: "500",
                    color: semanticColors.onDark.text.primary,
                    letterSpacing: 0.3,
                  }}
                >
                  {t("homepage.medical_history_step.continue")}
                </Text>
                <ArrowRight
                  size={15}
                  color={semanticColors.onDark.text.primary}
                  strokeWidth={2}
                />
              </LinearGradient>
            </View>
          )}
        </View>
      </Pressable>
    </View>
  );
}
